<template>
  <div>
    <SwitchOptions />

    <PriceDisplay
      v-bind="{
        basePrice,
        finalPrice,
      }"
    />

    <template v-if="!configuratorStore.isUploadConfigurator">
      <ConfiguratorFilters />

      <DefaultTextInput
        v-if="
          configuratorStore.isLetterCollageConfigurator &&
          !configuratorStore.mediaclipUserText
        "
        v-model="configuratorStore.mediaclipUserInputText"
        :label="I18n.global.t('pages.product_detail_page.user_text.label')"
        :required="true"
        class="mt-3"
        name="product_id"
        @update:model-value="handleMediaclipUserInputText"
      />

      <StickyBottomButton class="mt-8">
        <DefaultButton
          id="order-button"
          :loading="isInitializing"
          @click="startConfigurator"
        >
          <span
            v-if="configuratorStore.mediaclipLaunched"
            v-t="'pages.product_detail_page.continue_button.text'"
          />
          <span v-else v-t="'pages.product_detail_page.start_button.text'" />
        </DefaultButton>
      </StickyBottomButton>
      <FullPageLoader
        v-if="showLoader"
        :done="configuratorStore.currentStep !== ConfiguratorStep.ProductPage"
        :current-step="currentStep"
        @destroy-me="showLoader = false"
      />
    </template>

    <UploadButton v-else />

    <em
      v-if="configuratorStore.isUploadConfigurator"
      v-t="'pages.product_detail_page.after_upload_text'"
      class="mt-2 block text-center italic text-gray"
    />

    <InStock />

    <UspItems />

    <DeliveryOptions />
  </div>
</template>

<script setup lang="ts">
import StickyBottomButton from '@/components/Buttons/StickyBottomButton.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import FullPageLoader from '@/components/States/FullPageLoader.vue';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication';
import I18n from '@/i18n';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep';
import { computed, type ComputedRef, onBeforeMount, ref, watch } from 'vue';
import ConfiguratorFilters from '../Configurator/Filters/ConfiguratorFilters.vue';
import DeliveryOptions from './DeliveryOptions.vue';
import InStock from './InStock.vue';
import PriceDisplay from './PriceDisplay.vue';
import UploadButton from './UploadButton.vue';
import UspItems from './UspItems.vue';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';
import { usePaazlStore } from '@/stores/Paazl/PaazlStore.ts';
import DefaultTextInput from '@/components/Forms/DefaultTextInput.vue';
import type { PaazlDeliveryOptionsRequestObject } from '@/types/Paazl/PaazlDeliveryOptionsRequestObject.ts';
import SwitchOptions from '@/pages/ProductPage/partials/Collection/SwitchOptions.vue';

const configuratorStore = useConfiguratorStore();
const mediaclipCommunicationComposable = useMediaclipCommunication();
const isInitializing = ref(false);
const showLoader = ref(false);
const sizeStore = useSizeStore();
const paazlStore = usePaazlStore();

const genericMessage = I18n.global.t(
  'messages.error.upload_button.upload_error'
);

const paazlDeliveryOptionsRequestObject: ComputedRef<PaazlDeliveryOptionsRequestObject> =
  computed(() => ({
    sku: sizeStore.selectedSizeValue.attributes.sku!,
    shipping_cutoff_time:
      sizeStore.selectedSizeValue.attributes.shippingCutoffTime ?? undefined,
    processing_days: Number(
      sizeStore.selectedSizeValue.attributes.processingDays
    ),
    width: Number(sizeStore.selectedSizeValue.attributes.width),
    height: Number(sizeStore.selectedSizeValue.attributes.height),
    paazl_delivery_matrix:
      sizeStore.selectedSizeValue.attributes.paazlDeliveryMatrix ?? undefined,
    product_price: Number(sizeStore.selectedSizeValue.attributes.price),
  }));

const basePrice: ComputedRef<number | undefined> = computed(
  () =>
    (showLowestPrice.value
      ? sizeStore.selectedSizeValue.attributes.recommendedPrice
      : window.product_prices.base_price) / 100
);

const finalPrice: ComputedRef<number> = computed(
  () =>
    (showLowestPrice.value
      ? sizeStore.selectedSizeValue.attributes.price
      : window.product_prices.final_price) / 100
);

const showLowestPrice: ComputedRef<boolean> = computed(
  () =>
    !!configuratorStore.mediaclipProjectId ||
    (!configuratorStore.isUploadConfigurator &&
      sizeStore.hasMoreThanOneFilteredSize)
);

function showError(message: string) {
  window.dispatchMessages(
    [
      {
        type: 'error',
        text: message,
      },
    ],
    10000
  );
}

const startConfigurator = async () => {
  if (
    configuratorStore.isLetterCollageConfigurator &&
    !configuratorStore.mediaclipUserInputText &&
    !configuratorStore.mediaclipUserText
  ) {
    showError(I18n.global.t('pages.product_detail_page.user_text.error.empty'));
    return;
  }

  showLoader.value = true;
  isInitializing.value = true;

  try {
    await mediaclipCommunicationComposable.initMediaclip();
  } catch {
    showError(genericMessage);
    isInitializing.value = false;
    showLoader.value = false;
    return;
  }

  configuratorStore.setConfiguratorStep();
  isInitializing.value = false;

  mediaclipCommunicationComposable.setIdentifier();
};

const currentStep = computed(() => {
  return I18n.global.t(
    configuratorStore.isSimpleConfigurator
      ? 'loader.currentstep.starting_tool_simple'
      : 'loader.currentstep.starting_tool'
  );
});

const handleMediaclipUserInputText = () => {
  if (configuratorStore.mediaclipInitialized) {
    configuratorStore.resetMediaclipProject();
  }
};

onBeforeMount(() => {
  paazlStore.requestShippingOptions(paazlDeliveryOptionsRequestObject.value);
});

watch(
  () => sizeStore.selectedSizeOption,
  () => {
    paazlStore.requestShippingOptions(paazlDeliveryOptionsRequestObject.value);
  }
);
</script>
